import React, { FunctionComponent, useEffect, useState } from "react";
import * as TranslationApi from "../../api/translationApi";

import MaterialTable, { MTableToolbar } from "@material-table/core";

import Modal from "../../components/dialogs/modal";
import TextField from "@material-ui/core/TextField";
import { ProblemDetails } from "../../models/Http";
import Alert from "@material-ui/lab/Alert";
import { Language, TranslationV2 } from "../../models/Translation";
import { useSelector } from "react-redux";
import { AppState } from "../../store";
import { asList, useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchLanguages } from "../../store/languages/actions";
import { Chip, Grid, Paper } from "@material-ui/core";
import useStyles from "./styles";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";

interface State {
  upsertResult?: ProblemDetails;
  translations: readonly TranslationV2[];
}

export const TranslationsList: FunctionComponent = () => {
  const classes = useStyles();

  const languages = asList<Language>(
    useSelector<AppState, unknown>(s => s.languages)
  );

  useLoadIfEmpty(!languages.length, thunkFetchLanguages);

  const [isLoading, setIsLoading] = useState(true);
  const [state, setState] = useState<State>({
    translations: []
  });
  const [dialogOpenState, setDialogOpenState] = useState(false);
  const [translation, setTranslation] = useState<TranslationV2>({
    id: "",
    values: {}
  });
  const [filters, setFilters] = useState(
    languages.map((l: any) => ({
      ...l,
      active: l.code === "en"
    })) as any
  );

  useEffect(() => {
    if (languages.length && (!filters || !filters.length)) {
      setFilters(
        languages.map((l: any) => ({
          ...l,
          active: l.code === "en"
        }))
      );
    }
  }, [languages]);

  useEffect(() => {
    TranslationApi.list()
      .then(result => {
        setIsLoading(false);
        if (result.type == "OK") {
          setState({ ...state, translations: result.value });
        }
      })
      .catch(err => {});
  }, []);

  const populateTranslations = () => {
    let translations = [];
    for (let translation of state.translations) {
      let trans = { id: translation.id } as any;
      for (let language of languages) {
        trans[language.name] = translation.values
          ? translation.values[language.code]
          : null;
      }
      translations.push(trans);
    }
    return translations;
  };

  const populateColumnHeaders = () => {
    let columns = [{ title: "Key", field: "id" }];
    for (let language of filters) {
      if (language.active)
        columns.push({ title: language.name, field: language.name });
    }
    return columns;
  };

  const toggleFilter =
    (chip: any): any =>
    () => {
      const chipFilter = filters.map((f: any) => {
        if (chip.name === f.name) {
          f.active = !f.active;
        }
        return f;
      });

      setFilters(chipFilter);
    };

  return (
    <div>
      <MaterialTable
        title=""
        columns={populateColumnHeaders()}
        isLoading={isLoading}
        data={state.translations && populateTranslations()}
        actions={[
          {
            icon: "edit",
            tooltip: "Edit",
            onClick: (event, rowData: any) => {
              setTranslation(
                state.translations.find(x => x.id === rowData.id) ??
                  ({} as TranslationV2)
              );
              setDialogOpenState(true);
            }
          },
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: "Add new translation",
            isFreeAction: true,
            onClick: () => {
              setDialogOpenState(true);
            }
          }
        ]}
        options={{
          actionsColumnIndex: -1,
          pageSize: 100
        }}
        components={{
          Toolbar: props => (
            <div>
              <div
                style={{
                  float: "left",
                  position: "absolute",
                  padding: 20,
                  paddingTop: 25,
                  zIndex: 1000
                }}
              >
                {filters &&
                  filters.map((f: any) => (
                    <Chip
                      key={f.id}
                      label={f.name}
                      size={"small"}
                      className={f.active ? classes.chipActive : classes.chip}
                      onClick={toggleFilter(f)}
                    />
                  ))}
              </div>
              <MTableToolbar {...props} />
            </div>
          )
        }}
      />

      <Modal
        title={"Add or update translation"}
        open={dialogOpenState}
        actions={[
          {
            text: "Delete",
            type: "cancel",
            variant: "contained",
            startIcon: <DeleteIcon />,
            action: async () => {
              const result = await TranslationApi.remove(translation.id);
              if (result.type == "OK") {
                setTranslation({ id: "", values: {} });
                setState({ translations: result.value });
                setDialogOpenState(false);
              } else if (result.type == "ERROR") {
                setState({ ...state, upsertResult: result.details });
              }
            }
          },
          {
            component: <div style={{ flex: 1 }} />, // a spacer to keep the buttons spaced
            type: "component"
          },
          {
            text: "Save",
            type: "ok",
            startIcon: <SaveIcon />,
            disable: () => {
              return translation.id === "" || translation.values === null;
            },
            action: async () => {
              const result = await TranslationApi.upsert(translation);
              if (result.type == "OK") {
                setTranslation({ id: "", values: {} });
                setState({ translations: result.value });
                setDialogOpenState(false);
              } else if (result.type == "ERROR") {
                setState({ ...state, upsertResult: result.details });
              }
            }
          },
          {
            text: "Cancel",
            type: "cancel",
            action: () => {
              setDialogOpenState(false);
              setTranslation({ id: "", values: {} });
            }
          }
        ]}
      >
        <div>
          <TextField
            style={{ marginBottom: 20 }}
            fullWidth={true}
            label="Key"
            multiline
            rows={2}
            defaultValue={translation.id}
            variant="outlined"
            onChange={e => {
              setTranslation({ ...translation, id: e.target.value });
            }}
          />
          {filters ? (
            filters.map((f: any) => {
              if (!f.active) return <></>;
              return (
                <TextField
                  style={{ marginBottom: 20 }}
                  fullWidth={true}
                  label={f.name}
                  multiline
                  rows={4}
                  defaultValue={translation.values[f.code] ?? ""}
                  variant="outlined"
                  onChange={e => {
                    let translationValues = translation.values;
                    translationValues[f.code] = e.target.value;
                    setTranslation({
                      ...translation,
                      values: translationValues
                    });
                  }}
                />
              );
            })
          ) : (
            <></>
          )}
        </div>
        {state.upsertResult && (
          <Alert severity="error">{state.upsertResult}</Alert>
        )}
      </Modal>
    </div>
  );
};
